import { Navigate, Route, Routes, useLocation } from 'react-router-dom';

import { Spinner } from '@components/loading-spinner';
import { Stack } from '@components/stack';

import { BaseTab } from './common';
import { Header, HeaderProps } from './header';
import { HeaderActionsPortaled } from './portaled-header-actions';
import { HeaderLeftPortaled } from './portaled-header-left';
import { HeaderTabsPortaled } from './portaled-header-tabs';
import { RouterTabs } from './router-tabs';

export type RouterTab = BaseTab & {
  section: string;
};

export type RouterTabsHeaderProps = Omit<HeaderProps, 'tabs'> & {
  tabs: RouterTab[];
  hideTabs?: boolean;
  isLoading?: boolean;
  isPortaled?: boolean;
};

export const RouterTabsHeader = ({
  tabs,
  isLoading,
  isPortaled = false,
  ...rest
}: RouterTabsHeaderProps) => {
  return (
    <>
      {isPortaled ? (
        <>
          <HeaderLeftPortaled>
            <Stack space={2} orientation="horizontal" align="center">
              {rest.backAction}
              {rest.title ?? rest.breadcrumbs}
            </Stack>
          </HeaderLeftPortaled>
          <HeaderActionsPortaled>{rest.actions}</HeaderActionsPortaled>
          <HeaderTabsPortaled>
            <Tabs tabs={tabs} />
          </HeaderTabsPortaled>
        </>
      ) : (
        <Header tabs={<Tabs tabs={tabs} />} {...rest} />
      )}
      <Routes>
        {tabs.map((tab) => (
          <Route
            key={tab.section}
            path={tab.section}
            element={isLoading ? <Spinner /> : tab.content}
          />
        ))}
        <Route path="*" element={<Navigate to={tabs[0].section} replace />} />
      </Routes>
    </>
  );
};

type TabsProps = {
  tabs: RouterTab[];
};

const Tabs = ({ tabs }: TabsProps) => {
  const location = useLocation();
  const selectedSection = location.pathname.split('/').pop();

  return (
    <RouterTabs
      activeSection={selectedSection}
      tabs={tabs.map((tab) => ({ ...tab, to: tab.section }))}
    />
  );
};
